import { Button } from '@/components/ui/Button'
import { Icon } from '@/components/ui/Icon'
import { Info } from '@/components/ui/Info'
import { Modal } from '@/components/ui/Modal'
import { useRouteStore } from '@/store/route'
import { useSnackbarStore } from '@/store/snackbar'
import { StandardDonationStateAction } from '@types'
import { useState } from 'react'

interface HeadOutButtonProps {
  address: string
  donationId: number
  /** Icon-only round button for collapsed state */
  iconOnly?: boolean
  disabled?: boolean
  className?: string
}

export const HeadOutButton = ({
  address,
  donationId,
  iconOnly = false,
  disabled = false,
  className,
}: HeadOutButtonProps) => {
  const [isHeadOutModalOpen, setIsHeadOutModalOpen] = useState(false)
  const [isNavModalOpen, setIsNavModalOpen] = useState(false)
  const [loadingMap, setLoadingMap] = useState<
    'google' | 'apple' | 'waze' | null
  >(null)
  const updateDonationState = useRouteStore(
    (state) => state.updateDonationState
  )
  const showSnackbar = useSnackbarStore((state) => state.show)

  const handleMapSelection = async (
    mapType: 'google' | 'apple' | 'waze',
    openMap: () => void
  ) => {
    setLoadingMap(mapType)
    try {
      await updateDonationState(donationId, {
        state_action: StandardDonationStateAction.EN_ROUTE,
      })
      showSnackbar('Status updated to En Route', 'success')
      openMap()
      setIsNavModalOpen(false)
    } finally {
      setLoadingMap(null)
    }
  }

  const openGoogleMaps = () => {
    const encodedAddress = encodeURIComponent(address)
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`,
      '_blank'
    )
  }

  const openAppleMaps = () => {
    const encodedAddress = encodeURIComponent(address)
    window.open(`https://maps.apple.com/?daddr=${encodedAddress}`, '_blank')
  }

  const openWaze = () => {
    const encodedAddress = encodeURIComponent(address)
    window.open(
      `https://waze.com/ul?q=${encodedAddress}&navigate=yes`,
      '_blank'
    )
  }

  const handleConfirm = () => {
    setIsHeadOutModalOpen(false)
    setIsNavModalOpen(true)
  }

  return (
    <>
      {iconOnly ? (
        <Button
          icon={<Icon name="navigation" />}
          variant="primary"
          rounded
          onClick={() => setIsHeadOutModalOpen(true)}
          disabled={disabled}
          className={className}
          testId="head-out-button-icon"
        />
      ) : (
        <Button
          text="Head out"
          variant="primary"
          icon={<Icon name="navigation" size={16} />}
          onClick={() => setIsHeadOutModalOpen(true)}
          disabled={disabled}
          className={className}
          testId="head-out-button"
        />
      )}

      <Modal
        isOpen={isHeadOutModalOpen}
        onClose={() => setIsHeadOutModalOpen(false)}
        title="Heading out?"
        buttonLayout="row"
        testId="head-out-modal"
        buttons={[
          <Button
            key="back"
            text="Go back"
            variant="default"
            onClick={() => setIsHeadOutModalOpen(false)}
            testId="head-out-modal-back"
          />,
          <Button
            key="choose"
            text="Choose maps"
            variant="primary"
            icon={<Icon name="arrow" size={16} />}
            onClick={handleConfirm}
            testId="head-out-modal-choose"
          />,
        ]}
      >
        <Info>Donor will be notified you are en route.</Info>
      </Modal>

      <Modal
        isOpen={isNavModalOpen}
        onClose={() => setIsNavModalOpen(false)}
        title="Choose map for navigation"
        buttonLayout="column"
        testId="nav-modal"
        buttons={[
          <Button
            key="google"
            text="Google Maps"
            variant="light"
            icon={<Icon name="external-link" size={16} />}
            onClick={() => handleMapSelection('google', openGoogleMaps)}
            loading={loadingMap === 'google'}
            disabled={loadingMap !== null && loadingMap !== 'google'}
            testId="nav-modal-google"
          />,
          <Button
            key="apple"
            text="Apple Maps"
            variant="light"
            icon={<Icon name="external-link" size={16} />}
            onClick={() => handleMapSelection('apple', openAppleMaps)}
            loading={loadingMap === 'apple'}
            disabled={loadingMap !== null && loadingMap !== 'apple'}
            testId="nav-modal-apple"
          />,
          <Button
            key="waze"
            text="Waze"
            variant="light"
            icon={<Icon name="external-link" size={16} />}
            onClick={() => handleMapSelection('waze', openWaze)}
            loading={loadingMap === 'waze'}
            disabled={loadingMap !== null && loadingMap !== 'waze'}
            testId="nav-modal-waze"
          />,
          <Button
            key="back"
            text="Go back"
            variant="default"
            onClick={() => setIsNavModalOpen(false)}
            disabled={loadingMap !== null}
            testId="nav-modal-back"
          />,
        ]}
      />
    </>
  )
}
